export type ToolkitAction = { type: string; [key: string]: unknown };
type ToolkitActionListener = (action: ToolkitAction) => void;

/**
 * "Loud" subscribers. Fire only for actions originated on this instance via
 * {@link dispatchToolkitReduxAction}. The realtime bridge listens here to
 * publish actions outward (postMessage to other iframes; NATS to other clients).
 */
const actionListeners = new Set<ToolkitActionListener>();

/**
 * "Silent" subscribers. Fire for every action, both originated on this instance
 * and replayed from other instances (e.g. other iframes or other clients).
 */
const dispatchListeners = new Set<ToolkitActionListener>();

let initialized = false;

function notifyDispatchListeners(action: ToolkitAction) {
  for (const listener of dispatchListeners) {
    listener(action);
  }
}

export function initToolkitActionBus() {
  initialized = true;
}

/**
 * Subscribe to the "loud" stream (actions originated on this instance only),
 * useful to, e.g., publish them outward (which is what makes them "loud" under
 * our terminology).
 */
export function subscribeToolkitReduxActions(listener: ToolkitActionListener) {
  actionListeners.add(listener);

  return () => {
    actionListeners.delete(listener);
  };
}

/**
 * Subscribe to the "silent" stream (all actions, both originated locally and
 * replayed from other instances), useful to, e.g., mutate local state regardless
 * of where the action originated.
 */
export function subscribeToolkitReduxDispatches(listener: ToolkitActionListener) {
  dispatchListeners.add(listener);

  return () => {
    dispatchListeners.delete(listener);
  };
}

/**
 * Dispatch a replayed action (i.e., one that arrived from a peer over NATS or
 * iframe postMessage). Notifies silent subscribers only, so local state updates
 * but the realtime bridge does NOT re-publish it (which would echo back to
 * the original sender).
 */
export function dispatchToolkitReduxActionSilently(action: ToolkitAction) {
  if (!initialized) {
    throw new Error("Toolkit action bus has not been initialized yet.");
  }

  notifyDispatchListeners(action);
}

/**
 * Dispatch an action originated on this instance. Notifies silent
 * subscribers first (so local state updates), then loud subscribers (so the
 * realtime bridge publishes it to peers).
 */
export function dispatchToolkitReduxAction(action: ToolkitAction) {
  if (!initialized) {
    throw new Error("Toolkit action bus has not been initialized yet.");
  }

  notifyDispatchListeners(action);

  for (const listener of actionListeners) {
    listener(action);
  }
}

export function resetToolkitReduxStoreBridgeForTest() {
  actionListeners.clear();
  dispatchListeners.clear();
  initialized = false;
}
